"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Icon from "@/components/ui/AppIcon";

interface NavLink {
  id: string;
  label: string;
  href: string;
}

const navLinks: NavLink[] = [
  { id: "nav_home", label: "Home", href: "/homepage" },
  { id: "nav_it_consulting", label: "IT Consulting", href: "/services#it-consulting" },
  { id: "nav_services", label: "ITAD Services", href: "/services" },
  { id: "nav_locations", label: "Service Areas", href: "/locations" },
  { id: "nav_compliance", label: "Compliance", href: "/compliance-security" },
  { id: "nav_pricing", label: "Pricing", href: "/pricing" },
  { id: "nav_case_status", label: "Customer Portal", href: "/case-status" },
  { id: "nav_contact", label: "Get Started", href: "/create-a-case" },
  { id: "nav_admin", label: "Admin", href: "/admin/login" },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (href: string) => pathname === href;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "glass-panel border-b border-white/10 shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                <Icon name="ShieldCheckIcon" size={24} className="text-white" />
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-heading font-semibold text-foreground tracking-tight">
                Anchor Point
              </span>
              <span className="text-xs text-muted-foreground tracking-wider uppercase">
                Data Services
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                className={`relative text-sm font-medium transition-colors duration-300 ${
                  isActive(link.href)
                    ? "text-primary" :"text-muted-foreground hover:text-foreground"
                }`}
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" />
                )}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Link
              href="/create-a-case"
              className="btn btn-primary shadow-lg shadow-primary/20"
            >
              <Icon name="PlusCircleIcon" size={20} />
              Create a Case
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-foreground hover:text-primary transition-colors"
            aria-label="Toggle menu"
          >
            <Icon
              name={isMobileMenuOpen ? "XMarkIcon" : "Bars3Icon"}
              size={24}
            />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden pb-6 pt-4 border-t border-white/10 animate-fade-up bg-background/98 backdrop-blur-xl shadow-2xl rounded-b-2xl max-h-[calc(100vh-5rem)] overflow-y-auto custom-scrollbar">
            <nav className="flex flex-col gap-3 px-2">
              {navLinks.map((link) => (
                <Link
                  key={link.id}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-5 py-3.5 rounded-xl text-base font-medium transition-all shadow-sm ${
                    isActive(link.href)
                      ? "bg-primary/20 text-primary border border-primary/30 shadow-primary/10" :"text-foreground bg-card/80 hover:bg-muted hover:text-primary border border-white/5"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                href="/create-a-case"
                onClick={() => setIsMobileMenuOpen(false)}
                className="btn btn-primary mt-3 py-3.5 text-base shadow-lg"
              >
                <Icon name="PlusCircleIcon" size={20} />
                Create a Case
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}